import React, { useEffect, useMemo, useState } from "react";
import { db, auth } from "../../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { useBuilding } from "../../context/BuildingContext";
import { useToast } from "../../components/Toast";
import { isDemoUser, DEMO_BLOCK_MESSAGE } from "../../utils/demoMode";
import { formatCurrencyUY, formatDateUY } from "../../utils/formatters";
import { tolerantId } from "../../utils/firestoreHelpers";
import { getAllActiveFunds } from "../../utils/fundHelpers";
import { colors, radius, spacing, typography, btnPrimary, btnSecondary, btnDanger, pageContainer, pageTitle, pageSubtitle } from "../../utils/designTokens";
import StatusBadge from "../../components/StatusBadge";
import AdminModal from "../../components/AdminModal";
import DataTable from "../../components/DataTable";
import LoadingState from "../../components/LoadingState";
import EmptyState from "../../components/EmptyState";
import KPICard from "../../components/KPICard";
import {
  FaHandHoldingUsd,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
  FaUndo,
  FaExchangeAlt,
  FaReceipt,
  FaLock,
  FaSync,
} from "react-icons/fa";

/**
 * Gastos del edificio cargados por vecinos — panel del admin (oversight).
 *
 * El vecino aprueba y ya afecta la plata; el admin es la red de seguridad:
 *   - Rechazar un pendiente.
 *   - Revertir (una aprobación o un rechazo hecho sin querer → vuelve a pendiente;
 *     la Cloud Function deshace gasto + reintegro si estaba aprobado y NO fue
 *     liquidado por un cierre).
 *   - Cambiar el fondo del que sale (caja ↔ reserva/custom) → la CF actualiza el
 *     `expenses` vinculado (caja se reparte; reserva/custom debita el fondo).
 *
 * Bloqueos: una vez que el claim fue liquidado por un cierre (`consumedByCierreId`),
 * el revert y el cambio de fondo quedan deshabilitados (ajuste manual del admin).
 */

const STATUS_LABEL = { pending: "Pendiente", approved: "Aprobado", rejected: "Rechazado" };
const FILTERS = [
  { key: "todos", label: "Todos" },
  { key: "pending", label: "Pendientes" },
  { key: "approved", label: "Aprobados" },
  { key: "rejected", label: "Rechazados" },
];

const GastosVecinosAdmin = ({ setSelected }) => {
  const { selectedBuilding } = useBuilding();
  const { showToast } = useToast();
  const buildingId = selectedBuilding?.buildingid;

  const [loading, setLoading] = useState(true);
  const [claims, setClaims] = useState([]);
  const [filter, setFilter] = useState("todos");
  const [acting, setActing] = useState(false);

  const [fundModal, setFundModal] = useState(null);
  const [fundValue, setFundValue] = useState("caja");
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const funds = useMemo(() => getAllActiveFunds(selectedBuilding), [selectedBuilding]);
  const fundName = (id) => funds.find((f) => f.id === id)?.name || id || "Caja";

  const load = async () => {
    if (!buildingId) return;
    setLoading(true);
    try {
      const snap = await getDocs(
        query(collection(db, "expenseclaims"), where("buildingid", "in", tolerantId(buildingId)))
      );
      const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      all.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setClaims(all);
    } catch (e) {
      console.error("GastosVecinosAdmin: error cargando", e);
      showToast("No se pudieron cargar los gastos", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingId]);

  const guardDemo = async () => {
    if (await isDemoUser()) {
      showToast(DEMO_BLOCK_MESSAGE, "warning");
      return true;
    }
    return false;
  };

  const filtered = useMemo(
    () => (filter === "todos" ? claims : claims.filter((c) => c.status === filter)),
    [claims, filter]
  );

  const kpis = useMemo(() => {
    const pend = claims.filter((c) => c.status === "pending");
    const appr = claims.filter((c) => c.status === "approved");
    const reintegrado = appr
      .filter((c) => (c.currency || "UYU") !== "USD")
      .reduce((s, c) => s + (Number(c.amount) || 0), 0);
    return { pend: pend.length, appr: appr.length, reintegrado };
  }, [claims]);

  const doRevert = async (claim) => {
    if (await guardDemo()) return;
    if (claim.status === "approved" && claim.consumedByCierreId) {
      showToast("Ya fue liquidado por un cierre. No se puede revertir automáticamente.", "error");
      return;
    }
    setActing(true);
    try {
      await updateDoc(doc(db, "expenseclaims", claim.id), {
        status: "pending",
        revertedByEmail: auth.currentUser?.email || "admin",
        revertedByName: selectedBuilding?.name || "Administración",
        revertedAt: serverTimestamp(),
      });
      showToast(
        claim.status === "approved"
          ? "Aprobación revertida. Se deshizo el gasto y el reintegro."
          : "Rechazo revertido. El gasto vuelve a estar pendiente.",
        "success"
      );
      await load();
    } catch (e) {
      console.error("revert falló", e);
      showToast("No se pudo revertir", "error");
    } finally {
      setActing(false);
    }
  };

  const doReject = async () => {
    if (!rejectModal) return;
    if (await guardDemo()) return;
    setActing(true);
    try {
      await updateDoc(doc(db, "expenseclaims", rejectModal.id), {
        status: "rejected",
        rejectionReason: rejectReason.trim() || null,
        rejectedByEmail: auth.currentUser?.email || "admin",
        rejectedByName: selectedBuilding?.name || "Administración",
        rejectedAt: serverTimestamp(),
      });
      showToast("Gasto rechazado", "success");
      setRejectModal(null);
      setRejectReason("");
      await load();
    } catch (e) {
      console.error("reject falló", e);
      showToast("No se pudo rechazar", "error");
    } finally {
      setActing(false);
    }
  };

  const doChangeFund = async () => {
    if (!fundModal) return;
    if (await guardDemo()) return;
    if (fundModal.status === "approved" && fundModal.consumedByCierreId) {
      showToast("Ya fue liquidado por un cierre. No se puede cambiar el fondo.", "error");
      return;
    }
    setActing(true);
    try {
      await updateDoc(doc(db, "expenseclaims", fundModal.id), { sourceFund: fundValue });
      showToast(
        `Fondo cambiado a ${fundName(fundValue)}.` +
          (fundModal.status === "approved" ? " Se actualizó el gasto vinculado." : ""),
        "success"
      );
      setFundModal(null);
      await load();
    } catch (e) {
      console.error("change fund falló", e);
      showToast("No se pudo cambiar el fondo", "error");
    } finally {
      setActing(false);
    }
  };

  const columns = [
    { key: "concept", label: "Concepto", render: (v, r) => (
      <div>
        <div style={{ color: colors.black }}>{r.concept}</div>
        <div style={{ fontSize: 12, color: colors.gray400 }}>{r.userName || r.userEmail}{r.unitNumber ? ` · Unidad ${r.unitNumber}` : ""}</div>
      </div>
    ) },
    { key: "amount", label: "Monto", align: "right", render: (v, r) => (
      <span style={{ color: colors.black, whiteSpace: "nowrap" }}>
        {(r.currency === "USD" ? "US$ " : "") + formatCurrencyUY(Number(r.amount) || 0)}
      </span>
    ) },
    { key: "sourceFund", label: "Fondo", render: (v, r) => fundName(r.sourceFund) },
    { key: "status", label: "Estado", render: (v, r) => (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <StatusBadge status={r.status} label={STATUS_LABEL[r.status] || r.status} />
        {r.status === "approved" && r.consumedByCierreId && (
          <span title="Liquidado por un cierre" style={{ color: colors.gray400, fontSize: 12, display: "inline-flex", alignItems: "center", gap: 3 }}>
            <FaLock size={10} /> Liquidado
          </span>
        )}
      </span>
    ) },
    { key: "createdAt", label: "Fecha", render: (v, r) => r.createdAt?.seconds ? formatDateUY(new Date(r.createdAt.seconds * 1000)) : "—" },
    { key: "invoiceUrl", label: "Factura", align: "center", render: (v, r) => r.invoiceUrl ? (
      <a href={r.invoiceUrl} target="_blank" rel="noopener noreferrer" style={{ color: colors.primary }} title="Ver factura" aria-label="Ver factura">
        <FaReceipt />
      </a>
    ) : "—" },
    { key: "actions", label: "Acciones", align: "right", render: (v, r) => {
      const locked = r.status === "approved" && r.consumedByCierreId;
      return (
        <div style={{ display: "flex", gap: spacing[2], justifyContent: "flex-end", flexWrap: "wrap" }}>
          {r.status === "pending" && (
            <button style={rowBtn(btnDanger)} className="habitia-btn-danger" disabled={acting} onClick={() => { setRejectModal(r); setRejectReason(""); }}>
              <FaTimesCircle size={11} /> Rechazar
            </button>
          )}
          {(r.status === "approved" || r.status === "rejected") && (
            <button style={rowBtn(btnSecondary)} className="habitia-btn-secondary" disabled={acting || locked} onClick={() => doRevert(r)} title={locked ? "Liquidado por un cierre" : "Volver a pendiente"}>
              <FaUndo size={11} /> Revertir
            </button>
          )}
          {r.status !== "rejected" && !locked && (
            <button style={rowBtn(btnSecondary)} className="habitia-btn-secondary" disabled={acting} onClick={() => { setFundModal(r); setFundValue(r.sourceFund || "caja"); }}>
              <FaExchangeAlt size={11} /> Fondo
            </button>
          )}
        </div>
      );
    } },
  ];

  if (!buildingId) {
    return (
      <div style={pageContainer}>
        {/* EmptyState espera el COMPONENTE (icon={FaX}), no un elemento: con
            <FaX /> renderiza <Icon /> sobre un objeto y rompe la pantalla. */}
        <EmptyState icon={FaHandHoldingUsd} title="Seleccioná un edificio" description="Elegí un edificio para ver los gastos cargados por los vecinos." />
      </div>
    );
  }

  return (
    <div style={pageContainer}>
      {/* Header de página estándar del panel: título + subtítulo a la izquierda,
          acciones a la derecha (mismo patrón que el resto de las pantallas admin). */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: spacing[4], marginBottom: spacing[6], flexWrap: "wrap" }}>
        <div>
          <h1 style={pageTitle}>Gastos del edificio (vecinos)</h1>
          <p style={{ ...pageSubtitle, marginTop: 4, maxWidth: 720 }}>
            Gastos que los residentes pagaron y otro propietario aprobó. Podés rechazar, revertir o cambiar el fondo antes del cierre.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          style={{ ...btnSecondary, padding: "8px 16px", fontSize: 13, display: "inline-flex", alignItems: "center", gap: spacing[2], opacity: loading ? 0.6 : 1 }}
          className="habitia-btn-secondary"
        >
          <FaSync size={11} />
          Actualizar
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: spacing[4], marginBottom: spacing[8] }}>
        <KPICard icon={FaClock} iconBg={colors.warningLight} iconColor={colors.warningDark} label="Pendientes de aprobar" value={kpis.pend} />
        <KPICard icon={FaCheckCircle} iconBg={colors.successLight} iconColor={colors.successDark} label="Aprobados" value={kpis.appr} />
        <KPICard icon={FaHandHoldingUsd} label="Reintegrado ($)" value={formatCurrencyUY(kpis.reintegrado)} />
      </div>

      <div style={{ display: "flex", gap: spacing[2], marginBottom: spacing[3], flexWrap: "wrap" }}>
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={filter === f.key ? "habitia-btn-primary" : "habitia-btn-secondary"}
            style={{
              ...(filter === f.key ? btnPrimary : btnSecondary),
              padding: "8px 16px",
              fontSize: 13,
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <LoadingState />
      ) : (
        // DataTable ya trae su propia card (fondo blanco + sombra) y su
        // EmptyState integrado: no envolver en otra card ni duplicar el vacío.
        <DataTable
          columns={columns}
          data={filtered}
          emptyIcon={FaHandHoldingUsd}
          emptyTitle="Sin gastos"
          emptyDescription="No hay gastos de vecinos en este filtro."
        />
      )}

      {/* Modal cambiar fondo */}
      <AdminModal
        open={!!fundModal}
        onClose={() => setFundModal(null)}
        title="Cambiar el fondo del gasto"
        subtitle={fundModal ? `${fundModal.concept} · ${(fundModal.currency === "USD" ? "US$ " : "") + formatCurrencyUY(Number(fundModal.amount) || 0)}` : ""}
        footer={
          <>
            <button style={btnSecondary} className="habitia-btn-secondary" onClick={() => setFundModal(null)}>Cancelar</button>
            <button style={btnPrimary} className="habitia-btn-primary" disabled={acting} onClick={doChangeFund}>Guardar</button>
          </>
        }
      >
        <p style={{ color: colors.gray700, marginTop: 0 }}>
          <b>Caja</b>: el gasto se reparte entre todas las unidades. <b>Reserva u otro fondo</b>: se debita de ese fondo (no se reparte). En ambos casos el vecino recibe el reintegro completo.
        </p>
        <div style={{ display: "flex", gap: spacing[2], flexWrap: "wrap", marginTop: spacing[2] }}>
          {funds.map((f) => (
            <button
              key={f.id}
              onClick={() => setFundValue(f.id)}
              className={fundValue === f.id ? "habitia-btn-primary" : "habitia-btn-secondary"}
              style={{
                ...(fundValue === f.id ? btnPrimary : btnSecondary),
                padding: "8px 16px",
                fontSize: 13,
              }}
            >
              {f.name}
            </button>
          ))}
        </div>
      </AdminModal>

      {/* Modal rechazar */}
      <AdminModal
        open={!!rejectModal}
        onClose={() => setRejectModal(null)}
        title="Rechazar gasto"
        subtitle={rejectModal ? `${rejectModal.concept} · ${(rejectModal.currency === "USD" ? "US$ " : "") + formatCurrencyUY(Number(rejectModal.amount) || 0)}` : ""}
        footer={
          <>
            <button style={btnSecondary} className="habitia-btn-secondary" onClick={() => setRejectModal(null)}>Cancelar</button>
            <button style={btnDanger} className="habitia-btn-danger" disabled={acting} onClick={doReject}>Rechazar</button>
          </>
        }
      >
        <label htmlFor="gv-reject-reason" style={{ display: "block", fontSize: 13, color: colors.gray700, marginBottom: spacing[2] }}>
          Motivo (opcional)
        </label>
        <textarea
          id="gv-reject-reason"
          className="habitia-input"
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          rows={3}
          placeholder="Contale al vecino por qué lo rechazás"
          style={{
            width: "100%",
            boxSizing: "border-box",
            padding: "10px 12px",
            borderRadius: radius.md,
            border: `1px solid ${colors.gray200}`,
            fontFamily: typography.fontFamily,
            fontSize: 14,
            resize: "vertical",
          }}
        />
      </AdminModal>
    </div>
  );
};

// Variante compacta de los botones del sistema para acciones dentro de la
// tabla: misma paleta/variantes (btnDanger/btnSecondary), padding en escala.
const rowBtn = (base) => ({
  ...base,
  display: "inline-flex",
  alignItems: "center",
  gap: spacing[1],
  padding: "8px 12px",
  fontSize: 13,
  whiteSpace: "nowrap",
});

export default GastosVecinosAdmin;
